/**
 * THE CHART AS A THING YOU CAN PUT A HAND ON.
 *
 * -- WHAT WAS MISSING, IN ONE SENTENCE -----------------------------------------
 *
 * CONTRACT section 6: "THE PLAYER'S ONE VERB: choose what to fish for." Rounds 3 to 5 built
 * the sea, the boat and the chart that describes them, and nothing in the running game let a
 * player decide anything at all. The two navigation verbs had the same shape of hole: both
 * have lived on `VoyageSession` since round 2 with a browser-proof script as their only
 * caller. A verb whose only caller is a test is a verb the game does not have.
 *
 * This file is the hand. It owns no pixels and no session; it turns a place on the sheet and
 * a key on the keyboard into an INTENT, and applies an intent by calling a verb.
 *
 * -- WHY THE INTENT IS A VALUE ---------------------------------------------------
 *
 * The pointer path and the keyboard path have to end at the same call, or one of them
 * quietly does something the other cannot - which is the usual way a keyboard interface
 * turns out to be half an interface. They both produce a `ChartIntent`, and `apply` is the
 * only thing that reaches a verb. So a test can drive the whole surface by value, and the
 * browser has nothing of its own to get wrong.
 *
 * -- THE PRESENCE INVARIANT, AND WHY A CONTROL DOES NOT BREAK IT -----------------
 *
 * CONTRACT section 6: presence changes nothing, and src/sim/presence.test.ts holds it with a
 * never-inputs controller against an always-inputs controller. A control is an INPUT and the
 * things it sets are STATE: `targetSpecies` is a persisted field of the save, exactly like
 * `skill` (CONTRACT section 6, line 766), and the boat's order is persisted by
 * src/voyage/persistence.ts. So a player who sets a target and closes the tab earns what a
 * player who sets the same target and watches earns. Nothing in this file is called from the
 * draw path; every entry point is an event handler.
 */

import { SPECIES, type SimState } from '../sim/index';
import type { VoyageSession, VoyageView } from '../voyage/index';
import { chartLayout, inRect, LEDGER_ROWS, LEAVES, type ChartCopy, type Leaf, type LedgerRow } from './chart';
import { chosenLine, ledgerRows, lockedLine, placeRows, refusalLine } from './chart-copy';

/**
 * THE CONTRACT'S ONE PLAYER VERB, CALLED DIRECTLY.
 *
 * This used to be a shim. `VoyageSession` published VERB 1 `sailTo` and VERB 2
 * `returnToTrack` and nothing that set the target, so this surface reached the field the
 * long way round the same published API: export the envelope with `targetSpecies` spliced
 * in, splice the boat's order back on, and hand the string to `importText`. That was total
 * and it worked, and it REWROTE THE WHOLE SAVE - a fresh import, a new run identity, every
 * cache in the session invalidated - on every press of a ledger row.
 *
 * `VoyageSession.setTarget` is VERB 3 now, a pass-through to sim-core's own setter, so the
 * long way is gone and this is the call it always should have been. What is left here is
 * not a route to the field; it is the ANSWER THE CHART NEEDS: did the save take it. The
 * setter returns void because sim-core writes rather than reports, so the read-back is the
 * only way to ask, and an id the save's validator rejects (too long, or carrying a control
 * character) comes back as `''` and this returns false.
 */
export function setTargetSpecies(session: VoyageSession, speciesId: string, nowMs: number): boolean {
  session.setTarget(speciesId, nowMs);
  return session.state().targetSpecies === speciesId;
}

/** Which page of the ledger, its rows, and the id standing behind each row. */
export interface LedgerPage {
  readonly leaf: Leaf;
  readonly rows: readonly LedgerRow[];
  /** A species id, or a charted place id. Index-aligned with `rows`. */
  readonly ids: readonly string[];
}

/**
 * THE SPECIES LEAF - and THE COST OF TARGETING, WHICH THIS BUILD CANNOT SHOW.
 *
 * CONTRACT section 6 property 3 makes the choice cost PER-SPECIES MASTERY: hours spent
 * targeting A are hours not spent building mastery in B. `SimState` has no mastery field. It
 * carries `catches`, `xp`, `skill` and `targetSpecies`, and the only occurrences of the word
 * in the tree are forward-compatibility fixtures for a field that does not exist yet
 * (src/sim/save.test.ts, src/sim/never-nerf.test.ts). So the ledger shows NOTHING for it
 * rather than dressing the landed count up as mastery, which would be a number a player would
 * read as a cost that is not being charged. When the field lands it goes in this row's
 * right-hand column and nothing about the ledger's shape changes.
 */
export function speciesPage(state: SimState): LedgerPage {
  return { leaf: 'species', rows: ledgerRows(state), ids: SPECIES.map((s) => s.id) };
}

export function placesPage(view: VoyageView): LedgerPage {
  const found = view.chart.discovered.slice(0, LEDGER_ROWS * 2);
  return { leaf: 'places', rows: placeRows(found), ids: found.map((r) => r.id) };
}

export function pageFor(leaf: Leaf, state: SimState, view: VoyageView): LedgerPage {
  return leaf === 'species' ? speciesPage(state) : placesPage(view);
}

export type ChartIntent =
  | { readonly kind: 'none' }
  | { readonly kind: 'toggle' }
  | { readonly kind: 'close' }
  | { readonly kind: 'leaf'; readonly leaf: Leaf }
  | { readonly kind: 'cursor'; readonly to: number }
  | { readonly kind: 'choose'; readonly row: number }
  | { readonly kind: 'return' };

const NONE: ChartIntent = { kind: 'none' };

/**
 * WHAT A TOUCH AT (x, y) MEANS. Coordinates are art pixels measured from the sheet's own
 * first row of paper, which is what `chartLayout` publishes and what the host converts to.
 *
 * The order of the tests is the design: a hit on the LEDGER is a selection, and a hit
 * anywhere else on the sheet lifts or drops it. Round 5's handler toggled on any click in
 * the panel, so the first thing a player did after opening the ledger - press a row - shut it.
 */
export function hitAt(copy: ChartCopy, x: number, y: number): ChartIntent {
  const layout = chartLayout(copy);
  if (layout.exit !== null && inRect(layout.exit, x, y)) return { kind: 'return' };
  for (const h of layout.headings) {
    if (inRect(h.rect, x, y)) return h.leaf === copy.leaf ? NONE : { kind: 'leaf', leaf: h.leaf };
  }
  for (const row of layout.rows) {
    if (inRect(row.rect, x, y)) return { kind: 'choose', row: row.index };
  }
  return { kind: 'toggle' };
}

/**
 * WHAT A KEY MEANS. Every intent the pointer can produce, one of these can produce too.
 *
 * Up and down walk the whole ledger in reading order rather than one column, and left and
 * right turn the leaf. Two columns of twelve invite the 2-D mapping instead, with a separate
 * key for the page - but the only spare key for that is Tab, and swallowing Tab on a page
 * takes the browser's own focus ring away from the dev harness's buttons.
 */
export function keyIntent(key: string, copy: ChartCopy, rowCount: number): ChartIntent {
  const open = copy.lift > 0;
  if (key === 'l' || key === 'L') return { kind: 'toggle' };
  if (key === 'Escape') return open ? { kind: 'close' } : NONE;
  if ((key === 'r' || key === 'R') && copy.exit) return { kind: 'return' };
  if (!open || rowCount === 0) return NONE;
  const at = copy.cursor < 0 ? 0 : copy.cursor;
  if (key === 'ArrowDown') return { kind: 'cursor', to: (at + 1) % rowCount };
  if (key === 'ArrowUp') return { kind: 'cursor', to: (at - 1 + rowCount) % rowCount };
  if (key === 'ArrowLeft' || key === 'ArrowRight') {
    const i = LEAVES.indexOf(copy.leaf);
    const step = key === 'ArrowRight' ? 1 : LEAVES.length - 1;
    return { kind: 'leaf', leaf: LEAVES[(i + step) % LEAVES.length] ?? copy.leaf };
  }
  if (key === 'Enter' || key === ' ') return { kind: 'choose', row: at };
  return NONE;
}

/** Keys this surface consumes while the ledger is open, so the page does not scroll under it. */
export const SWALLOWED_KEYS: readonly string[] = ['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', ' ', 'Enter'];

/**
 * CHOOSING A ROW: the only place in the renderer that calls a verb.
 *
 * Returns the sentence the chart should say, or null when the change speaks for itself -
 * choosing a landfall rewrites the phrase and puts the strike on the strip, which is louder
 * than a line of prose about it.
 */
export function choose(session: VoyageSession, page: LedgerPage, index: number, nowMs: number): string | null {
  const row = page.rows[index];
  const id = page.ids[index];
  if (row === undefined || id === undefined) return null;

  if (page.leaf === 'species') {
    if (!row.reachable) return lockedLine(id);
    if (row.marked) return null;
    return setTargetSpecies(session, id, nowMs) ? chosenLine(id) : 'The chart would not take that.';
  }

  if (row.marked) return 'The boat is lying there already.';
  const result = session.sailTo(id, nowMs);
  return result.ok ? null : refusalLine(result.reason);
}

